import { useEffect, useState } from "react";
import { Card, Table, Input, Button, Tag, message } from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  Wallet,
  Coins,
  TrendingUp,
  CreditCard,
  RefreshCcw,
} from "lucide-react";
import reapi from "../../../api/reapi";

export default function AdminPayments() {
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState("");

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const fetchPayments = async () => {
    try {
      setLoading(true);

      const skip = (page - 1) * limit;
      const token = localStorage.getItem("meeshoadminToken");

      const res = await reapi.get(
        `/adm/payments?skip=${skip}&limit=${limit}&search=${search}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPayments(res.data.data.payments || []);
      setStats(res.data.data.stats || {});
      setTotal(res.data.data.total || 0);
    } catch (error) {
      message.error(error?.response?.data?.message || "Payments load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [page, limit]);

  const columns = [
    {
      title: "#",
      width: 60,
      render: (_, __, index) => (page - 1) * limit + index + 1,
    },
    {
      title: "User",
      width: 220,
      render: (_, record) => (
        <div>
          <b>{record.userName || "User"}</b>
          <div className="text-slate-500 text-sm">{record.userEmail || "—"}</div>
        </div>
      ),
    },
    {
      title: "Order ID",
      dataIndex: "txnid",
      width: 180,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      width: 120,
      render: (v) => <b>₹{v || 0}</b>,
    },
    {
      title: "Credits Added",
      dataIndex: "amount",
      width: 140,
      render: (v) => v || 0,
    },
    {
      title: "Payment Method",
      dataIndex: "PG",
      width: 160,
      render: (v) => v || "Cashfree",
    },
    {
      title: "Payment ID",
      dataIndex: "refid",
      width: 200,
      render: (v) => v || "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (status) => {
        if (status === "success") return <Tag color="success">Success</Tag>;
        if (status === "pending") return <Tag color="warning">Pending</Tag>;
        return <Tag color="error">Failed</Tag>;
      },
    },
    {
      title: "Date & Time",
      dataIndex: "createdAt",
      width: 200,
      render: (date) =>
        new Date(date).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
    {
      title: "Actions",
      width: 100,
      render: () => <Button>View</Button>,
    },
  ];

  return (
    <div>
      <div className="mb-7">
        <h1 className="text-4xl font-extrabold m-0">Payments</h1>
        <p className="text-slate-500 mt-2">
          View all payments and credit purchases.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <Input
          size="large"
          placeholder="Search by user name, email or order id..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={() => {
            setPage(1);
            fetchPayments();
          }}
          className="rounded-xl"
        />

        <Button size="large" icon={<FilterOutlined />} className="h-12 px-8">
          Filter
        </Button>

        <Button
          type="primary"
          size="large"
          icon={<DownloadOutlined />}
          className="h-12 px-8 bg-purple-600"
        >
          Export
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-5 mb-6">
        <StatCard
          icon={<Wallet />}
          title="Total Revenue"
          value={`₹${stats.totalRevenue || 0}`}
          color="purple"
        />

        <StatCard
          icon={<Coins />}
          title="Total Payments"
          value={stats.totalPayments || 0}
          color="green"
        />

        <StatCard
          icon={<TrendingUp />}
          title="Credits Sold"
          value={stats.creditsSold || 0}
          color="blue"
        />

        <StatCard
          icon={<CreditCard />}
          title="Avg. Order Value"
          value={`₹${stats.avgOrderValue || 0}`}
          color="orange"
        />

        <StatCard
          icon={<RefreshCcw />}
          title="Refunds / Failed"
          value={stats.refunds || 0}
          color="red"
          danger
        />
      </div>

      <Card className="rounded-2xl shadow-sm overflow-hidden">
        <div className="w-full overflow-x-auto">
          <Table
            loading={loading}
            columns={columns}
            dataSource={payments}
            rowKey="_id"
            pagination={{
              current: page,
              pageSize: limit,
              total,
              showSizeChanger: true,
              onChange: (p, ps) => {
                setPage(p);
                setLimit(ps);
              },
            }}
            scroll={{ x: 1400 }}
          />
        </div>
      </Card>
    </div>
  );
}

function StatCard({ icon, title, value, color, danger }) {
  const colors = {
    purple: "bg-purple-100 text-purple-600",
    green: "bg-green-100 text-green-600",
    blue: "bg-blue-100 text-blue-600",
    orange: "bg-orange-100 text-orange-600",
    red: "bg-red-100 text-red-600",
  };

  return (
    <Card className="rounded-2xl shadow-sm">
      <div className="flex items-center gap-5">
        <div
          className={`w-16 h-16 rounded-xl flex items-center justify-center ${colors[color]}`}
        >
          {icon}
        </div>

        <div>
          <p className="text-slate-500 m-0">{title}</p>
          <h2 className="text-3xl font-extrabold m-0 mt-2">{value}</h2>
          <p className={danger ? "text-red-500 m-0 mt-2" : "text-green-600 m-0 mt-2"}>
            {danger ? "↓ 7.1%" : "↑ 15.8%"}
          </p>
        </div>
      </div>
    </Card>
  );
}